import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { ImageLightboxMemo } from '@/components/property/ImageLightbox';
import { PropertyVideoPlayer } from '@/components/property/PropertyVideoPlayer';
import { Loader2, Bed, Bath, Square, Calendar } from 'lucide-react';
import type { Id } from '../../convex/_generated/dataModel';
import type { IPropertyDetailContentProps } from '@/@types/property';

function PropertyImageThumbnail({
  storageId,
  index,
  onClick,
}: {
  storageId: Id<'_storage'>;
  index: number;
  onClick: () => void;
}) {
  const imageUrl = useQuery(api.storage.getUrl, { storageId });

  return (
    <button
      type="button"
      onClick={onClick}
      className="aspect-square bg-gray-200 dark:bg-gray-800 rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500"
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={`Property image ${index + 1}`}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
        </div>
      )}
    </button>
  );
}

export const PropertyDetailContent = ({
  property,
  latestVideo,
  hasCompletedVideo,
  lightboxOpen,
  lightboxIndex,
  onImageClick,
  onLightboxClose,
}: IPropertyDetailContentProps) => {
  return (
    <>
      {property.imageIds.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
            Images ({property.imageIds.length})
          </h2>
          <div className="grid grid-cols-4 gap-2">
            {property.imageIds.slice(0, 8).map((imageId: Id<'_storage'>, index: number) => (
              <PropertyImageThumbnail
                key={imageId}
                storageId={imageId}
                index={index}
                onClick={() => onImageClick(index)}
              />
            ))}
            {property.imageIds.length > 8 && (
              <button
                type="button"
                onClick={() => onImageClick(8)}
                className="aspect-square bg-gray-200 dark:bg-gray-800 rounded-lg flex items-center justify-center text-gray-500 text-sm cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                +{property.imageIds.length - 8} more
              </button>
            )}
          </div>

          <ImageLightboxMemo
            imageIds={property.imageIds}
            initialIndex={lightboxIndex}
            isOpen={lightboxOpen}
            onClose={onLightboxClose}
          />
        </div>
      )}

      {hasCompletedVideo && latestVideo?.finalVideoId && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
            Generated Video
          </h2>
          <PropertyVideoPlayer
            finalVideoId={latestVideo.finalVideoId}
            durationSeconds={latestVideo.finalVideoDurationSeconds}
            fileSizeBytes={latestVideo.finalVideoSizeBytes}
            propertyId={property._id}
            propertyTitle={property.title}
          />
        </div>
      )}

      <div className="mb-6">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
          Property Details
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {property.bedrooms !== undefined && (
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
              <Bed className="h-4 w-4" />
              <span>{property.bedrooms} Beds</span>
            </div>
          )}
          {property.bathrooms !== undefined && (
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
              <Bath className="h-4 w-4" />
              <span>{property.bathrooms} Baths</span>
            </div>
          )}
          {property.squareMeters !== undefined && (
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
              <Square className="h-4 w-4" />
              <span>{property.squareMeters} m&sup2;</span>
            </div>
          )}
          {property.yearBuilt !== undefined && (
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
              <Calendar className="h-4 w-4" />
              <span>Built {property.yearBuilt}</span>
            </div>
          )}
        </div>
      </div>

      {property.features && property.features.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Features</h2>
          <div className="flex flex-wrap gap-2">
            {property.features.map((feature: string) => (
              <span
                key={feature}
                className="px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm rounded-full"
              >
                {feature}
              </span>
            ))}
          </div>
        </div>
      )}

      {property.characteristics && Object.keys(property.characteristics).length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
            Listing Details
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Object.entries(property.characteristics).map(([key, value]: [string, string]) => (
              <div
                key={key}
                className="flex justify-between items-center py-2 px-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg"
              >
                <span className="text-sm text-gray-500 dark:text-gray-400">{key}</span>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {value}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {property.generatedDescription && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
            Generated Description
          </h2>
          <div className="p-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg">
            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
              {property.generatedDescription}
            </p>
          </div>
        </div>
      )}
    </>
  );
};
